import { useMemo } from 'react'

import { AlertActivityChange, type AlertActivityType } from '@/api/types/Alert'
import { useGetUser } from '@/api/user'
import { formatAsTitle } from '@/utils/format'

const changeTextCopy: Record<AlertActivityChange, string> = {
  [AlertActivityChange.STATUS]: 'updated the status',
  [AlertActivityChange.ASSIGNMENT]: 'assigned the alert',
  [AlertActivityChange.ESCALATED]: 'escalated the alert',
  [AlertActivityChange.TYPE]: 'updated the alert type',
  [AlertActivityChange.SELF_EVALUATION]: 'updated the self-evaluation',
}

const useAlertActivity = (activity: AlertActivityType) => {
  const activityCreatorId = typeof activity.user === 'string'
    ? activity.user
    : activity.user?.id || undefined
  const { data: activityCreatorData } = useGetUser(activityCreatorId)
  const activityCreator = activityCreatorData?.data?.user

  const oldAssignedToId = typeof activity.from?.assignedTo === 'string'
    ? activity.from?.assignedTo
    : activity.from?.assignedTo?.id || undefined
  const { data: oldAssignedToData } = useGetUser(oldAssignedToId)
  const oldAssignedTo = oldAssignedToData?.data?.user

  const newAssignedToId = typeof activity.to?.assignedTo === 'string'
    ? activity.to?.assignedTo
    : activity.to?.assignedTo?.id || undefined
  const { data: newAssignedToData } = useGetUser(newAssignedToId)
  const newAssignedTo = newAssignedToData?.data?.user

  const oldEscalateToId = typeof activity.from?.escalateTo === 'string'
    ? activity.from?.escalateTo
    : activity.from?.escalateTo?.id || undefined
  const { data: oldEscalateToData } = useGetUser(oldEscalateToId)
  const oldEscalateTo = oldEscalateToData?.data?.user

  const newEscalateToId = typeof activity.to?.escalateTo === 'string'
    ? activity.to?.escalateTo
    : activity.to?.escalateTo?.id || undefined
  const { data: newEscalateToData } = useGetUser(newEscalateToId)
  const newEscalateTo = newEscalateToData?.data?.user

  const activityMessage = useMemo(() => {
    const changeText = changeTextCopy[activity.changeType as AlertActivityChange] || 'updated the alert'

    const creatorName = <b>{activityCreator?.details.fullName || 'Someone'}</b>
    const assignmentFromName = <b>{oldAssignedTo?.details.fullName || 'undefined user'}</b>
    const assignmentToName = <b>{newAssignedTo?.details.fullName || 'undefined user'}</b>
    const escalateFromName = <b>{oldEscalateTo?.details.fullName || 'undefined user'}</b>
    const escalateToName = <b>{newEscalateTo?.details.fullName || 'undefined user'}</b>

    const oldStatusName = <b>{formatAsTitle(activity.from.status)}</b>
    const newStatusName = <b>{formatAsTitle(activity.to.status)}</b>

    const oldTypes = <b>{(activity.from.alertDetails?.usageType || []).map(formatAsTitle).join(', ')}</b>
    const newTypes = <b>{(activity.to.alertDetails?.usageType || []).map(formatAsTitle).join(', ')}</b>

    const evaluationString = [] as React.ReactNode[]
    if (activity.changeType === AlertActivityChange.SELF_EVALUATION) {
      const keys = ['notMe', 'authorized', 'threat'] as const
      keys.forEach(key => {
        const fromValue = activity.from.alertDetails?.[key]
        const toValue = activity.to.alertDetails?.[key]
        if (fromValue !== toValue) {
          const fromString = fromValue === null || fromValue === undefined ? 'undefined' : fromValue.toString()
          const toString = toValue === null || toValue === undefined ? 'undefined' : toValue.toString()

          const from = <code>{fromString}</code>
          const to = <code>{toString}</code>

          evaluationString.push(
            <span key={key}>
              <b>{formatAsTitle(key)}</b> changed from {from} to {to} {' '}
            </span>
          )
        }
      })
    }

    switch (activity.changeType) {
      case AlertActivityChange.STATUS:
        return <>
          {creatorName} {changeText} from {oldStatusName} to {newStatusName}.
        </>
      case AlertActivityChange.TYPE:
        return <>
          {creatorName} {changeText} from {oldTypes} to {newTypes}.
        </>
      case AlertActivityChange.ASSIGNMENT:
        return <>
          {creatorName} {changeText} from {assignmentFromName} to {assignmentToName}.
        </>
      case AlertActivityChange.ESCALATED:
        return <>
          {creatorName} {changeText} from {escalateFromName} to {escalateToName}.
        </>
      case AlertActivityChange.SELF_EVALUATION:
        return <>
          {creatorName} {changeText}: <br /> {evaluationString}.
        </>
      default:
        return <>
          {creatorName} {changeText}
        </>
    }
  }, [
    activity,
    activityCreator?.details.fullName,
    newAssignedTo?.details.fullName,
    newEscalateTo?.details.fullName,
    oldAssignedTo?.details.fullName,
    oldEscalateTo?.details.fullName
  ])
  return {
    activityCreator,
    activityMessage
  }
}

export default useAlertActivity
